// screens-flow.jsx — pushed screens: JobDetail, NavigateScreen, CreateFlow, ReportScreen
import React from 'react';
import {
  kr, num, fmtMin, fmtHM, STATUS, STATUS_ORDER, ARTICLES, artByCode,
  TEMPLATES, JOBS, VAT, rowAmount, reportTotal, rowLabel, rowUnit, rowRate,
  TODAY, isoLocal, fmtDateLong, fmtDateShort, APP, COMPANY, uid,
  WORK_START_MIN, WORK_END_MIN, hmToMin, minToHM, ceilMin, lunchWindow, lunchAbsorb, groupParts, nextGroupId,
  travelBetween, prevJobSameDay, resolveDeparture,
  Btn, StatusBadge, SecLabel, Card, Avatar, AppHeader, TabBar, TabItem,
  Stepper, Field, Sheet, MapMock, CalendarSheet, Toggle, ProfileButton,
  Icon, ICON_PATHS,
} from '../shared.js';
const { useState: useStateF, useEffect: useEffectF } = React;

const STRIPE = 'repeating-linear-gradient(135deg,#eef1f1 0 9px,#e6eaeb 9px 18px)';

// ── Info row helper ─────────────────────────────────────────
function InfoRow({ icon, label, value, mono, last }) {
  return (
    <div className="gv-lrow" style={{ borderBottom: last ? 'none' : '1px solid var(--line)' }}>
      <div className="gv-lrow-ic"><Icon name={icon} size={18} stroke={1.9} /></div>
      <div className="gv-lrow-tt"><span style={{ fontSize: 12.5 }}>{label}</span><b style={{ fontSize: 15, fontWeight: 650, fontFamily: mono ? 'var(--mono)' : 'inherit' }}>{value}</b></div>
    </div>
  );
}

// ── Report read-only summary (used in detail) ───────────────
function ReportSummary({ report }) {
  const sum = reportTotal(report);
  return (
    <div className="gv-list">
      {report.rows.map((r, i) => (
        <div key={r.id || i} className="gv-lrow">
          <div className="gv-lrow-tt" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <b style={{ fontSize: 14.5, fontWeight: 600 }}>{rowLabel(r)}</b>
              <span className="gv-tnum" style={{ display: 'block', fontSize: 12.5 }}>{num(r.qty, 1)} {rowUnit(r)} × {kr(rowRate(r))}</span>
            </div>
            <span className="gv-tnum" style={{ fontWeight: 700, fontSize: 14.5 }}>{kr(rowAmount(r))}</span>
          </div>
        </div>
      ))}
      <div className="gv-lrow" style={{ background: 'var(--surface-2)' }}>
        <div className="gv-lrow-tt" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <b style={{ fontSize: 14.5 }}>Summa exkl. moms</b>
          <span className="gv-tnum" style={{ fontWeight: 800, fontSize: 16 }}>{kr(sum)}</span>
        </div>
      </div>
    </div>
  );
}

// ── JOB DETAIL ──────────────────────────────────────────────
function NotifySheet({ job, open, onClose, onSend }) {
  const now = new Date();
  const nowHM = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const eta = addMinutes(nowHM, job.travelMinutes);
  const tpls = [
    { key: 'pavag', label: 'På väg nu', text: `Hej! Jag är på väg till er och beräknas vara framme om ca ${job.travelMinutes} min (~${eta}). Vänliga hälsningar, ${COMPANY.user}, ${COMPANY.name}.` },
    { key: 'strax', label: 'Strax framme', text: `Hej! Jag är snart framme hos er, ca 5 minuter kvar. Vänliga hälsningar, ${COMPANY.user}.` },
    { key: 'forsenad', label: 'Försenad ~15 min', text: `Hej! Jag blir tyvärr ca 15 minuter försenad. Ny beräknad ankomst ~${addMinutes(eta, 15)}. Vänliga hälsningar, ${COMPANY.user}.` },
  ];
  const [tpl, setTpl] = useStateF('pavag');
  const [text, setText] = useStateF(tpls[0].text);
  const pick = (t) => { setTpl(t.key); setText(t.text); };

  return (
    <Sheet open={open} onClose={onClose} title="Meddela kund" maxH="88%">
      <div className="gv-list" style={{ marginBottom: 14 }}>
        <div className="gv-lrow">
          <div className="gv-lrow-ic"><Icon name="user" size={18} stroke={1.9} /></div>
          <div className="gv-lrow-tt"><b style={{ fontSize: 14.5, fontWeight: 650 }}>{job.customer}</b><span>{job.address.split(',')[0]}</span></div>
        </div>
        <div className="gv-lrow">
          <div className="gv-lrow-ic" style={{ background: 'var(--accent-wash)', color: 'var(--accent-ink)' }}><Icon name="clock" size={18} stroke={1.9} /></div>
          <div className="gv-lrow-tt"><span style={{ fontSize: 12.5 }}>Beräknad ankomst</span><b className="gv-tnum" style={{ fontSize: 15, fontWeight: 650 }}>~{eta} · om {job.travelMinutes} min</b></div>
        </div>
      </div>

      <div style={{ fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--ink-3)', marginBottom: 9 }}>Snabbval</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
        {tpls.map(t => (
          <button key={t.key} onClick={() => pick(t)} className={'gv-chip' + (tpl === t.key ? ' is-on' : '')} style={{ height: 36 }}>{t.label}</button>
        ))}
      </div>

      <Field label="Meddelande" value={text} onChange={setText} multiline />
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, margin: '10px 2px 18px', color: 'var(--ink-3)', fontSize: 12.5 }}>
        <Icon name="message" size={14} stroke={1.9} /> Skickas som SMS och app-notis till kunden
      </div>
      <Btn variant="primary" icon="send" full onClick={() => onSend(text)}>Skicka till {job.customer.split(' ')[0]}</Btn>
    </Sheet>
  );
}

// Edit the core details of a job (title/customer/contact/description; time + duration for planned jobs).
function EditJobSheet({ job, open, onClose, onSave }) {
  const planned = job.status === 'planerad';
  const [title, setTitle] = useStateF(job.title || '');
  const [customer, setCustomer] = useStateF(job.customer || '');
  const [phone, setPhone] = useStateF(job.phone || '');
  const [email, setEmail] = useStateF(job.email || '');
  const [startTime, setStartTime] = useStateF(job.startTime || '08:00');
  const [estHours, setEstHours] = useStateF((job.estMinutes || 60) / 60);
  const [desc, setDesc] = useStateF(job.description && job.description !== '—' ? job.description : '');

  // Re-seed from the job every time the sheet is opened so a cancelled edit is discarded.
  useEffectF(() => {
    if (!open) return;
    setTitle(job.title || '');
    setCustomer(job.customer || '');
    setPhone(job.phone || '');
    setEmail(job.email || '');
    setStartTime(job.startTime || '08:00');
    setEstHours((job.estMinutes || 60) / 60);
    setDesc(job.description && job.description !== '—' ? job.description : '');
  }, [open]);

  const save = () => {
    const patch = {
      title: title.trim() || job.title,
      customer: customer.trim() || job.customer,
      phone: phone.trim(),
      email: email.trim(),
      description: desc.trim() || '—',
    };
    if (planned) {
      patch.startTime = startTime;
      patch.estMinutes = Math.round(estHours * 60);
    }
    onSave(patch);
  };

  return (
    <Sheet open={open} onClose={onClose} title="Redigera uppdrag" maxH="90%">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingBottom: 4 }}>
        <Field label="Rubrik" value={title} onChange={setTitle} placeholder="t.ex. Byte av varmvattenberedare" />
        <Field label="Kund" value={customer} onChange={setCustomer} placeholder="Kundens namn" />
        <Field label="Telefon" value={phone} onChange={setPhone} placeholder="070-123 45 67" type="tel" mono />
        <Field label="E-post" value={email} onChange={setEmail} placeholder="namn@exempel.se" type="email" />
        {planned && (
          <div style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}><Field label="Starttid" value={startTime} onChange={setStartTime} mono /></div>
            <div style={{ flex: 1 }}>
              <span className="gv-label" style={{ display: 'block', marginBottom: 7 }}>Beräknad tid</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Stepper value={estHours} onChange={setEstHours} step={0.5} min={0.5} />
                <span className="gv-affix" style={{ flexShrink: 0 }}>tim</span>
              </div>
            </div>
          </div>
        )}
        <Field label="Beskrivning" value={desc} onChange={setDesc} placeholder="Vad ska göras?" multiline />
      </div>
      <Btn variant="primary" icon="check" full onClick={save} style={{ marginTop: 18 }}>Spara ändringar</Btn>
    </Sheet>
  );
}

function JobDetail({ job, jobs = [], onOpenPart, onBack, onNavigate, onReport, onInvoice, onNotify, onEdit }) {
  const [notify, setNotify] = useStateF(false);
  const [editOpen, setEditOpen] = useStateF(false);
  const reported = job.status === 'rapporterad' || job.status === 'fakturerad';
  const parts = job.groupId ? groupParts(jobs, job.groupId) : [];
  const dep = resolveDeparture(job, jobs);
  const depTag = dep.basis === 'previous' ? 'föreg. uppdrag' : dep.basis === 'gps' ? 'GPS' : 'verkstad';

  return (
    <div className="gv-app">
      <AppHeader onBack={onBack} subtitle={job.id} title="Uppdrag"
        right={onEdit ? <button className="gv-iconbtn" aria-label="Redigera" onClick={() => setEditOpen(true)}><Icon name="edit" size={20} stroke={1.9} /></button> : null} />
      <div className="gv-scroll has-footer">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <StatusBadge status={job.status} />
          <span className="gv-tnum" style={{ fontSize: 13, color: 'var(--ink-2)' }}>{fmtDateShort(job.date)} · {job.startTime}</span>
        </div>
        <div style={{ fontSize: 23, fontWeight: 780, letterSpacing: '-.02em', lineHeight: 1.15 }}>{job.title}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
          <span style={{ fontSize: 15, color: 'var(--ink-2)' }}>{job.customer}</span>
          {job.partTotal > 1 && <span className="gv-badge is-sm" style={{ background: 'var(--accent-wash)', color: 'var(--accent-ink)' }}><Icon name="layers" size={11} stroke={2} /> Del {job.partIndex} av {job.partTotal}</span>}
        </div>

        <div style={{ marginTop: 16 }}>
          <MapMock pin={job.pin} height={166} label={job.address} />
        </div>
        {job.status === 'planerad' ? (
          <>
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <Btn variant="secondary" icon="message" onClick={() => setNotify(true)} style={{ flex: 1 }}>Meddela kund</Btn>
              <Btn variant="secondary" icon="navigate" onClick={onNavigate} style={{ flex: 1 }}>Navigera dit</Btn>
            </div>
            {job.notifiedAt && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 10, color: 'var(--accent-ink)', fontSize: 13, fontWeight: 650 }}>
                <Icon name="checkCircle" size={15} stroke={2} /> Kund meddelad {job.notifiedAt}
              </div>
            )}
          </>
        ) : (
          <Btn variant="secondary" icon="navigate" full onClick={onNavigate} style={{ marginTop: 10 }}>Navigera dit</Btn>
        )}

        {parts.length > 1 && <>
          <SecLabel action={`${parts.length} besök`}>Uppdraget i flera delar</SecLabel>
          <div className="gv-list">
            {parts.map(p => (
              <div key={p.id} className={'gv-lrow' + (p.id !== job.id ? ' is-tap' : '')} onClick={p.id !== job.id ? () => onOpenPart && onOpenPart(p) : undefined} style={p.id === job.id ? { background: 'var(--surface-2)' } : null}>
                <div className="gv-lrow-ic" style={{ background: p.id === job.id ? 'var(--accent-wash)' : 'var(--surface-2)', color: p.id === job.id ? 'var(--accent-ink)' : 'var(--ink-2)', fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 13 }}>{p.partIndex}</div>
                <div className="gv-lrow-tt" style={{ flex: 1 }}>
                  <b style={{ fontSize: 14, fontWeight: 650 }}>{fmtDateShort(p.date)} · {p.startTime}</b>
                  <span style={{ fontSize: 12.5 }}>{fmtMin(p.estMinutes)} · {STATUS[p.status].label}{p.id === job.id ? ' · denna' : ''}</span>
                </div>
                {p.id === job.id ? <Icon name="checkCircle" size={18} color="var(--accent)" stroke={2} /> : <Icon name="chevR" size={16} color="var(--ink-3)" stroke={2} />}
              </div>
            ))}
          </div>
        </>}

        <SecLabel>Detaljer</SecLabel>
        <div className="gv-list">
          <InfoRow icon="pin" label="Adress" value={job.address} />
          <InfoRow icon="route" label={job.fromMode === 'auto' ? `Utgår från · ${depTag}` : 'Utgår från'} value={dep.label} />
          <InfoRow icon="car" label={dep.basis === 'previous' ? 'Restid från föreg. uppdrag' : 'Restid (uppskattad)'} value={`${dep.min} min · ${num(dep.km, 1)} km`} mono />
          <InfoRow icon="clock" label="Beräknad arbetstid" value={fmtMin(job.estMinutes)} last />
        </div>

        {(job.phone || job.email) && <>
          <SecLabel>Kontakt</SecLabel>
          <div className="gv-list">
            {job.phone && <a href={`tel:${job.phone}`} className="gv-lrow is-tap" style={{ textDecoration: 'none', color: 'inherit' }}>
              <div className="gv-lrow-ic"><Icon name="phone" size={18} stroke={1.9} /></div>
              <div className="gv-lrow-tt"><span style={{ fontSize: 12.5 }}>Telefon</span><b style={{ fontSize: 15, fontWeight: 650, fontFamily: 'var(--mono)' }}>{job.phone}</b></div>
              <Icon name="chevR" size={16} color="var(--ink-3)" stroke={2} />
            </a>}
            {job.email && <a href={`mailto:${job.email}`} className="gv-lrow is-tap" style={{ textDecoration: 'none', color: 'inherit', borderBottom: 'none' }}>
              <div className="gv-lrow-ic"><Icon name="mail" size={18} stroke={1.9} /></div>
              <div className="gv-lrow-tt"><span style={{ fontSize: 12.5 }}>E-post</span><b style={{ fontSize: 15, fontWeight: 650 }}>{job.email}</b></div>
              <Icon name="chevR" size={16} color="var(--ink-3)" stroke={2} />
            </a>}
          </div>
        </>}

        <SecLabel>Beskrivning</SecLabel>
        <Card style={{ fontSize: 14.5, lineHeight: 1.5, color: 'var(--ink-2)' }}>{job.description}</Card>

        {job.materials && job.materials.length > 0 && <>
          <SecLabel action={`${job.materials.length} st`}>Material som behövs</SecLabel>
          <div className="gv-list">
            {job.materials.map((m, i) => (
              <div key={i} className="gv-lrow">
                <div className="gv-lrow-ic"><Icon name="box" size={18} stroke={1.9} /></div>
                <div className="gv-lrow-tt" style={{ flex: 1 }}><b style={{ fontSize: 14.5, fontWeight: 600 }}>{m.name}</b></div>
                <span className="gv-tnum" style={{ fontSize: 14, color: 'var(--ink-2)' }}>{num(m.qty, 0)} {m.unit}</span>
              </div>
            ))}
          </div>
        </>}

        {reported && job.report && <>
          <SecLabel action={job.invoiceNo}>Rapport</SecLabel>
          {job.report.photos && job.report.photos.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              {job.report.photos.map((p, i) => (
                <div key={i} style={{ width: 66, height: 66, borderRadius: 10, background: STRIPE, border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name="camera" size={20} stroke={1.8} color="var(--ink-3)" />
                </div>
              ))}
            </div>
          )}
          <ReportSummary report={job.report} />
        </>}
      </div>

      {/* footer actions by status */}
      <div className="gv-footer">
        {job.status === 'planerad' && <>
          <Btn variant="secondary" icon="navigate" onClick={onNavigate} style={{ flex: 1 }}>Navigera</Btn>
          <Btn variant="primary" icon="doc" onClick={() => onReport(job)} style={{ flex: 1.3 }}>Rapportera</Btn>
        </>}
        {job.status === 'rapporterad' && <>
          <Btn variant="secondary" icon="doc" onClick={() => onReport(job)} style={{ flex: 1 }}>Redigera</Btn>
          <Btn variant="primary" icon="check" onClick={() => onInvoice(job)} style={{ flex: 1.3 }}>Markera fakturerad</Btn>
        </>}
        {job.status === 'fakturerad' && (
          <Btn variant="secondary" icon="doc" full onClick={() => onReport(job)}>Visa rapport</Btn>
        )}
      </div>

      <NotifySheet job={job} open={notify} onClose={() => setNotify(false)} onSend={(msg) => { setNotify(false); onNotify && onNotify(job, msg); }} />
      {onEdit && <EditJobSheet job={job} open={editOpen} onClose={() => setEditOpen(false)} onSave={(patch) => { setEditOpen(false); onEdit(job, patch); }} />}
    </div>
  );
}

// ── NAVIGATE (full-screen map) ──────────────────────────────
function NavigateScreen({ job, onBack, onArrive }) {
  return (
    <div className="gv-app" style={{ background: '#e9ede9' }}>
      <div style={{ position: 'absolute', inset: 0 }}>
        <MapMock pin={job.pin} route height="100%" />
      </div>
      {/* floating back */}
      <div style={{ position: 'absolute', top: 56, left: 18, right: 18, zIndex: 10, display: 'flex', justifyContent: 'space-between' }}>
        <button className="gv-iconbtn" onClick={onBack} style={{ boxShadow: '0 2px 8px rgba(0,0,0,.15)' }}><Icon name="back" size={22} stroke={2.2} /></button>
        <div style={{ background: 'rgba(255,255,255,.95)', borderRadius: 11, padding: '8px 13px', boxShadow: '0 2px 8px rgba(0,0,0,.12)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="navigate" size={16} color="var(--accent)" stroke={2.2} />
          <span style={{ fontWeight: 700, fontSize: 13.5 }}>Snabbaste vägen</span>
        </div>
      </div>

      {/* turn banner */}
      <div style={{ position: 'absolute', top: 110, left: 18, right: 18, zIndex: 10, background: 'var(--ink)', color: '#fff', borderRadius: 16, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, boxShadow: '0 8px 24px rgba(0,0,0,.22)' }}>
        <Icon name="arrowR" size={28} stroke={2.4} />
        <div>
          <div className="gv-tnum" style={{ fontSize: 22, fontWeight: 780 }}>450 m</div>
          <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.7)' }}>Sväng höger mot {job.address.split(',')[0]}</div>
        </div>
      </div>

      {/* bottom card */}
      <div style={{ marginTop: 'auto', position: 'relative', zIndex: 10, background: 'var(--surface)', borderRadius: '24px 24px 0 0', padding: '18px 20px 30px', boxShadow: '0 -8px 30px rgba(0,0,0,.12)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div>
            <div className="gv-tnum" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-.02em', color: 'var(--accent-ink)' }}>{job.travelMinutes} min</div>
            <div className="gv-tnum" style={{ fontSize: 13.5, color: 'var(--ink-2)', marginTop: 1 }}>{num(job.travelKm, 1)} km · ank. {addMinutes(job.startTime, -job.travelMinutes)}</div>
          </div>
          <div style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
            <div style={{ fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{job.address.split(',')[0]}</div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>{job.customer}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Btn variant="secondary" icon="phone" onClick={onBack} style={{ flex: 1 }}>Avbryt</Btn>
          <Btn variant="primary" icon="checkCircle" onClick={onArrive} style={{ flex: 1.5 }}>Jag är framme</Btn>
        </div>
      </div>
    </div>
  );
}
function addMinutes(hm, delta) {
  const [h, m] = hm.split(':').map(Number);
  let t = h * 60 + m + delta;
  t = ((t % 1440) + 1440) % 1440;
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

export { InfoRow, ReportSummary, NotifySheet, JobDetail, NavigateScreen, addMinutes, STRIPE };
